/**
 * DashboardStatisticsChart Component
 *
 * Monthly statistics bar chart for Recursos Dashboard overview.
 * Shows Completos (green) vs Pendentes (yellow) for 7 months.
 *
 * Design specs from Figma (Dashboard Overview):
 * - Card: Full width in left column
 * - Background: #15203C
 * - Border radius: 20px
 * - Padding: 30px
 * - Bar colors: #09B068 (completos - green), #F2C71E (pendentes - yellow)
 * - Matches status badge colors
 * - 7 months of data
 *
 * Differences from MonthlyStatsBarChart (detail page):
 * - Green/Yellow colors instead of Purple
 * - Adapted for dashboard overview layout
 */

import { MonthlyStatistic } from "@/types/recursos-dashboard.interface";
import { BarChart, Bar, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { Skeleton } from "@/components/ui/skeleton";

interface DashboardStatisticsChartProps {
  /** Monthly statistics data (7 months) */
  data: MonthlyStatistic[];

  /** Optional period label */
  period?: string;

  /** Loading state */
  isLoading?: boolean;
}

export function DashboardStatisticsChart({
  data,
  period = "Últimos 7 meses",
  isLoading = false,
}: DashboardStatisticsChartProps) {
  return (
    <div className="bg-[#15203C] rounded-[20px] p-[30px] flex flex-col h-[400px]">
      {/* Header */}
      <div className="flex items-center justify-between mb-[10px]">
        <h3 className="text-[20px] font-semibold text-[#F6F6F6]">
          Estatísticas
        </h3>
        <p className="text-[12px] text-[#888888]">{period}</p>
      </div>

      {/* Chart */}
      <div className="flex-1 min-h-[200px]">
        {isLoading ? (
          <Skeleton className="h-full" />
        ) : data.length === 0 ? (
          <div className="flex items-center justify-center h-full text-gray-400 text-sm">
            Sem dados disponíveis
          </div>
        ) : (
          <ResponsiveContainer width="100%" height="100%" minHeight={200}>
            <BarChart
              data={data}
              barGap={5}
              barCategoryGap={20}
              margin={{ top: 20, right: 0, left: 0, bottom: 20 }}
            >
              <XAxis
                dataKey="month"
                tick={{ fill: "#FFFFFF", fontSize: 12 }}
                axisLine={false}
                tickLine={false}
              />
              <YAxis
                tick={{ fill: "#FFFFFF", fontSize: 12 }}
                axisLine={false}
                tickLine={false}
                tickFormatter={(value) => {
                  if (value >= 100000) return `${value / 1000}k`;
                  if (value >= 1000) return `${value / 1000}k`;
                  return `${value}`;
                }}
              />
              <Tooltip
                contentStyle={{
                  backgroundColor: "#15203C",
                  border: "1px solid #5D5D5D",
                  borderRadius: "8px",
                }}
                labelStyle={{ color: "#F6F6F6" }}
                itemStyle={{ color: "#F6F6F6" }}
                formatter={(value: number) => [
                  new Intl.NumberFormat("pt-BR", {
                    style: "currency",
                    currency: "BRL",
                  }).format(value),
                  "",
                ]}
              />
              {/* Completos - Purple (matching Figma design) */}
              <Bar
                dataKey="completos"
                fill="#7846FF"
                radius={[4, 4, 0, 0]}
                barSize={20}
              />
              {/* Pendentes - Dark Purple (matching Figma design) */}
              <Bar
                dataKey="pendentes"
                fill="#45169C"
                radius={[4, 4, 0, 0]}
                barSize={20}
              />
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>

      {/* Legend */}
      <div className="flex items-center gap-6 mt-3">
        <div className="flex items-center gap-[10px]">
          <div className="size-[7px] rounded-full bg-[#7846FF]" />
          <p className="text-[12px] text-white">Completos</p>
        </div>
        <div className="flex items-center gap-[10px]">
          <div className="size-[7px] rounded-full bg-[#45169C]" />
          <p className="text-[12px] text-white">Pendentes</p>
        </div>
      </div>
    </div>
  );
}
